import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ticketActions } from "../../features/ticket/ticketSlice";
import type { TimeEntryEditPayload } from "../components/EditTimeEntryModal";
import { canEditTimeEntry, editableRowId } from "./canEditTimeEntry";
import type { TimeEntryGroup, TimeEntryRow } from "./groupTimeEntries";

type TimeEntryEditorArgs = {
  ticketId: string;
  ticketStatus?: string | null;
  currentUserId?: number;
  currentUserGroups?: string[];
  // state.ticket.editTimeEntryStatus — read by the caller, which owns the typed store.
  status: string;
};

/**
 * Modal wiring shared by the four lists that can edit a time entry (sessions and task
 * logs, in each app). The app-specific selectors stay with the caller; only the action
 * creator, which is shared, is dispatched from here.
 */
export function useTimeEntryEditor<Row extends TimeEntryRow>({
  ticketId,
  ticketStatus,
  currentUserId,
  currentUserGroups,
  status,
}: TimeEntryEditorArgs) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState<TimeEntryGroup<Row> | null>(null);

  useEffect(() => {
    if (status === "succeeded") {
      setEditing(null);
    }
  }, [status]);

  const canEdit = (group: TimeEntryGroup<Row>) =>
    canEditTimeEntry({
      sessionId: group.sessionId,
      ticketStatus,
      assigneeIds: group.assigneeIds,
      currentUserId,
      currentUserGroups,
    });

  const openEditor = (group: TimeEntryGroup<Row>) => {
    // A previous edit leaves the status at "succeeded", which would close this one
    // the moment it opened.
    dispatch(ticketActions.setEditTimeEntryStatus("idle"));
    setEditing(group);
  };

  const submitEdit = (payload: TimeEntryEditPayload) => {
    if (editing === null) {
      return;
    }
    const entryId = editableRowId(editing, currentUserId);
    if (entryId === undefined) {
      return;
    }
    dispatch(ticketActions.editTimeEntry({ entryId, ticketId, ...payload }));
  };

  return {
    editing,
    loading: status === "loading",
    canEdit,
    openEditor,
    closeEditor: () => setEditing(null),
    submitEdit,
  };
}
